import { CHOIX_DE_PRIORITE } from '@nextilt/commun';
import type { Priorite } from '../partage/api/types';
import { useMenuOuvert } from '../partage/useMenuOuvert';
import {
  AUCUNE_EN_AVANT,
  METTRE_EN_AVANT,
  MOTS_PRIORITE,
} from '../partage/vocabulaire';
import './ParametresDeFeuille.css';

/**
 * Le bouton ⚙, à côté des vues (ticket 13 de retours-testeurs) : les réglages de la feuille, rangés hors de la tête.
 * « Hiérarchiser » y est venu (décision d'Igor du 2026-09-27) ; la touche h reste son
 * raccourci, tenu par `useHierarchisation`, et le menu la rappelle.
 *
 * Dessous, le calque (ticket 04 du chantier `priorites`) : une importance mise en avant
 * (Urgent, Important, Quand tu veux, chantier `flottaison`), ou rien ; en Hiérarchie
 * seulement (la Liste se range déjà par importance). Le choix tient tant qu'on reste dans le
 * dossier (`calqueDuDossier.ts`).
 *
 * Depuis le chantier `revue-design` (tickets 03 et 04), ⚙ n'est plus qu'en Hiérarchie sur
 * l'ordinateur : en Carte, le filtre en pastilles et « Hiérarchiser » sont dans la barre de la
 * carte (`BarreDeLaCarte`). Au téléphone, il reste en Carte et en Hiérarchie, sans calque.
 */
export function ParametresDeFeuille({
  hierarchisable,
  onHierarchiser,
  calque,
}: {
  /** Rien à ordonner sous deux bulles, et rien à proposer pendant qu'on ordonne déjà. */
  hierarchisable: boolean;
  onHierarchiser: () => void;
  /** Le calque de priorité ; absent en Liste. */
  calque?: { enAvant: Priorite | null; choisir: (enAvant: Priorite | null) => void };
}) {
  const menu = useMenuOuvert();
  const enAvant = calque?.enAvant ?? null;

  return (
    <div className="feuille-parametres" ref={menu.ref}>
      <button
        type="button"
        className={`feuille-parametres-bouton${enAvant ? ' feuille-parametres-bouton--calque' : ''}`}
        data-tuto="parametres-de-feuille"
        aria-haspopup="menu"
        aria-expanded={menu.ouvert}
        aria-label="paramètres"
        title={enAvant ? `Paramètres · ${MOTS_PRIORITE[enAvant]} en avant` : 'Paramètres'}
        onClick={() => menu.setOuvert(!menu.ouvert)}
      >
        ⚙
      </button>

      {menu.ouvert && (
        <div className="feuille-parametres-menu" role="menu">
          <button
            type="button"
            role="menuitem"
            className="feuille-parametres-hierarchiser"
            data-tuto="hierarchiser"
            disabled={!hierarchisable}
            title={hierarchisable ? 'Ou la touche h' : 'Il faut au moins deux bulles à ranger'}
            onClick={() => {
              menu.setOuvert(false);
              onHierarchiser();
            }}
          >
            Hiérarchiser <kbd>h</kbd>
          </button>

          {calque && (
            <div className="feuille-parametres-calque" role="group" aria-label={METTRE_EN_AVANT}>
              <span className="feuille-parametres-titre">{METTRE_EN_AVANT}</span>
              {[null, ...CHOIX_DE_PRIORITE].map((p) => (
                <button
                  key={p ?? 'aucune'}
                  type="button"
                  role="menuitemradio"
                  aria-checked={enAvant === p}
                  onClick={() => {
                    menu.setOuvert(false);
                    calque.choisir(p);
                  }}
                >
                  {p ? MOTS_PRIORITE[p] : AUCUNE_EN_AVANT}
                  {enAvant === p && <span aria-hidden="true">✓</span>}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
